'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import useDelayedLoader from '@src/quince-site-common/src/hooks/useDelayedLoader'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import PageBuilder, { ContainerConfig, PageContext } from '@src/quince-site-common/src/organisms/pageBuilder'
import wishListStateManager from '@src/quince-site-common/src/redux/globalStateManager/WishlistStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { ADDITIONAL_CUSTOM_SECTIONS } from '@src/quince-site-landing/src/templates/partnerPDPLandingPage/partnerPDPLandingPage.constant'
import SectionRenderer from '@src/SectionRenderer'
import { PageDataResponse } from '@src/server-side/api/api.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { shouldUseSvdCard } from '@src/utils/should-use-svd-card'

import { fetchSvdWishlist } from './svd/svd-wishlist.helpers'
import WishlistItemListSvd from './svd/wishlist-item-list-svd'
import WishlistHeading from './wishlist-components/wishlist-heading'
import WishlistItemList from './wishlist-components/wishlist-item-list'
import { matchProductListTitleToSearch } from './wishlist-page.helpers'
import { fetchSharedWishList, fetchUserWishListProducts } from './wishlist.helpers'
import { SharedWishListDataType, WishlistItem } from './wishlist.types'

import type { SvdWishlistPageData } from './svd/svd-wishlist.types'

export interface ProductFromContentful {
  contentful_id: string
}

const Wishlist = ({ data, isMobile }: { data: PageDataResponse<PageContext<ContainerConfig>>; isMobile: boolean }) => {
  const pageData = data.context?.pageDataJson
  const isInit = useRef(false)
  // Tracks the (id, wishlistTotalItemsCount) our own SVD fetch last produced, so the fetch effect
  // below can tell "count changed because our fetch just wrote it" (skip) apart from "count changed
  // for some other reason, e.g. a heart add/remove on this page" (refetch). `id` is tracked too —
  // switching between self and a shared wishlist (header wishlist icon while viewing a share link)
  // changes `id` without touching the viewer's own count, and must always refetch.
  const svdAppliedCountRef = useRef<number | null>(null)
  const svdAppliedIdRef = useRef<string | null>(null)
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const searchParams = useSearchParams()

  const id: string | null = searchParams.get('id')

  const {
    fetchData: fetchWishListItems,
    isLoading: isWishListLoading,
    response,
    hasError,
    error,
  } = useFetch<WishlistItem[], [boolean]>(fetchUserWishListProducts, {
    initialData: [],
    initialLoadingState: true,
  })

  const {
    fetchData: fetchShareWishListItems,
    isLoading: isShareWishListLoading,
    response: shareWishListResponse,
    hasError: shareWishListHasError,
    error: shareWishListError,
  } = useFetch<SharedWishListDataType, [string, boolean]>(fetchSharedWishList, {
    initialData: { name: '', wishListItems: [] },
    initialLoadingState: true,
  })

  const {
    fetchData: fetchSvdItems,
    isLoading: isSvdLoading,
    response: svdResponse,
    hasError: svdHasError,
    error: svdError,
  } = useFetch<SvdWishlistPageData, [string | null]>(fetchSvdWishlist, {
    initialData: { cards: [] },
    initialLoadingState: true,
  })

  // Under the SVD flow the wishlist page renders SVD cards for BOTH self and shared, via `/v2`
  // (embedded hydrated cards). `shouldUseSvdCard()` is the same global gate the product-card
  // sections use; the legacy path is untouched for non-SVD users.
  const isSvd = shouldUseSvdCard()

  const name = isSvd ? svdResponse?.name : id ? shareWishListResponse?.name : undefined
  const wishListItems = id ? shareWishListResponse?.wishListItems : response

  const wishlistTotalItemsCount = useAppSelector(wishListStateManager, wishListStateManager.getWishlistTotalItemsCount)

  useEffect(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.WISHLIST_PAGE_VIEW, {})
  }, [])

  useEffect(() => {
    if (hasError) {
      captureException(error, {
        module: MODULES.WISHLIST,
        feature: FEATURES.FETCH_WISHLIST,
        extraInfo: {
          // @i18n-ignore
          capturedFrom: 'fetchWishListItems in WishlistItemList',
        },
      })
    }
  }, [error, hasError])

  useEffect(() => {
    if (shareWishListHasError) {
      captureException(shareWishListError, {
        module: MODULES.WISHLIST,
        feature: FEATURES.FETCH_WISHLIST,
        extraInfo: {
          // @i18n-ignore
          capturedFrom: 'fetchShareWishListItems in WishlistItemList',
        },
      })
    }
  }, [shareWishListError, shareWishListHasError])

  useEffect(() => {
    if (svdHasError) {
      captureException(svdError, {
        module: MODULES.WISHLIST,
        feature: FEATURES.FETCH_WISHLIST,
        extraInfo: {
          // @i18n-ignore
          capturedFrom: 'fetchSvdItems in Wishlist',
        },
      })
    }
  }, [svdError, svdHasError])

  useEffect(() => {
    // SVD self+shared fetch. wishlistTotalItemsCount IS a dep — a heart add/remove on this page
    // (e.g. removing a card from the wishlist grid) writes that same count, and this is the only
    // signal that tells the page to refetch and reflect the change. But fetchSvdItems (via
    // mergeSvdWishlistOnLogin) ALSO writes that count when it completes, which would otherwise
    // self-retrigger this effect on every load (count: 0 -> real count) for a second, pointless
    // get-active-wishlist-products + bulk-update-wishlist round-trip. svdAppliedCountRef
    // distinguishes the two: skip when the count already matches what our own last fetch produced,
    // fetch when it's a genuinely external change. Legacy fetch below is untouched.
    if (!isSvd) return
    if (svdAppliedIdRef.current === id && svdAppliedCountRef.current === wishlistTotalItemsCount) return
    fetchSvdItems(id)
      .catch((e: unknown) => {
        console.error('Error while fetching SVD wishlist items, fetchSvdItems() function:: ', e)
      })
      .finally(() => {
        svdAppliedIdRef.current = id
        svdAppliedCountRef.current = wishListStateManager.getWishlistTotalItemsCount()
      })
  }, [id, isSvd, fetchSvdItems, wishlistTotalItemsCount])

  useEffect(() => {
    // Legacy user-wishlist fetch only runs when SVD is off.
    if (isSvd) return
    if (!id) {
      fetchWishListItems(isRecurringItemSubscriptionsExpEnabled).catch((e: unknown) => {
        console.error('Error while fetching wishList items, fetchWishListItems() function:: ', e)
      })
    }
  }, [wishlistTotalItemsCount, id, isSvd, fetchWishListItems, isRecurringItemSubscriptionsExpEnabled])

  useEffect(() => {
    // Legacy shared fetch only when SVD is off (SVD shared goes through fetchSvdItems above).
    if (!isSvd && id) {
      fetchShareWishListItems(id, isRecurringItemSubscriptionsExpEnabled).catch((e: unknown) => {
        console.error('error while fetching shared wishList items, fetchShareWishListItems() function:: ', e)
      })
    }
  }, [fetchShareWishListItems, id, isSvd, isRecurringItemSubscriptionsExpEnabled])

  const renderSection = SectionRenderer({
    // @i18n-ignore
    tracker: 'landingPage',
    additionalCustomSections: ADDITIONAL_CUSTOM_SECTIONS,
    pageSubType: pageData?.pageSubType,
  })

  const sectionRenderer = (section: CustomSection) => renderSection(matchProductListTitleToSearch(section))

  const isSelfWishlist = !id
  const hasData = isSvd
    ? (svdResponse?.cards.length ?? 0) > 0
    : id
      ? (shareWishListResponse?.wishListItems.length ?? 0) > 0
      : (response?.length ?? 0) > 0
  const isFetching = isSvd ? isSvdLoading : id ? isShareWishListLoading : isWishListLoading

  // Use delayed loader to avoid flash on fast background refetches
  const isLoading = useDelayedLoader(isFetching, hasData, { threshold: 300 })

  const wishlistProductList =
    (isSvd
      ? svdResponse?.cards.map(({ product, selectedVariantId }) => {
          const selectedVariant =
            product.cardVariants.find(variant => variant.variantId === selectedVariantId) ?? product.cardVariants[0]
          return {
            product_id: product.relatedIds.spid,
            color: selectedVariant?.displayConfig?.value,
          }
        })
      : wishListItems?.map(productCard => {
          const selectedCardVariant = getInitialSelectedVariant(productCard.product)
          const selectedColor = selectedCardVariant?.displayConfig.value
          return {
            product_id: productCard.product.relatedIds.spid,
            color: selectedColor,
          }
        })) ?? []

  useEffect(() => {
    if (isInit.current) return
    if (!isLoading) {
      const eventToFire = isSelfWishlist
        ? TELEMETRY_EVENT_NAMES.WISHLIST_LOAD
        : TELEMETRY_EVENT_NAMES.SHARED_WISHLIST_LOAD
      trackEvent(eventToFire, {
        wishlistProductList: wishlistProductList,
      })
      isInit.current = true
    }
  }, [wishListItems, isLoading, isSelfWishlist])

  return (
    <>
      <WishlistHeading isSelfWishlist={isSelfWishlist} name={name} wishListProductList={wishlistProductList} />
      {isSvd ? (
        <WishlistItemListSvd
          items={svdResponse?.cards}
          isLoading={isLoading}
          sectionConfig={{
            // @i18n-ignore
            tracker: 'landingPage',
          }}
          isSelfWishlist={isSelfWishlist}
        />
      ) : (
        <WishlistItemList
          items={wishListItems}
          isLoading={isLoading}
          sectionConfig={{
            // @i18n-ignore
            tracker: 'landingPage',
          }}
          isSelfWishlist={isSelfWishlist}
        />
      )}
      {pageData && (
        <PageBuilder
          context={pageData}
          selfContainerRenderer={sectionRenderer}
          isMobile={isMobile}
          className={'hide-horizontal-scroll'}
        />
      )}
    </>
  )
}

export default Wishlist
